import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Question } from '../../services/models/question.model';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SafeHtml, DomSanitizer } from '@angular/platform-browser';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SharedService } from '../../services/shared.service';
@Component({
  selector: 'app-question-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatRadioModule,
    MatCheckboxModule,
    MatTooltipModule,
    CommonModule,
  ],
  templateUrl: './question-dialog.component.html',
  styleUrl: './question-dialog.component.scss',
})
export class QuestionDialogComponent implements OnInit, OnDestroy {
  question: Question;
  form: FormGroup;
  result: {
    correct: boolean;
    message?: string;
    nextTimeCode?: number;
    nextQuestionId?: number;
  } = { correct: false };
  public showRationale = false;
  sanitizedHtml: SafeHtml | undefined;
  selectedOptions: number[] = [];
  buttonDisabled = true;

  constructor(
    private dialogRef: MatDialogRef<QuestionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { question: Question },
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private sharedService: SharedService
  ) {
    this.question = data.question;

    if (this.question.html) {
      this.sanitizedHtml = this.sanitizer.bypassSecurityTrustHtml(
        this.question.html
      );
    }

    this.form = this.fb.group({
      singleChoice: [''],
      multipleChoices: this.fb.array([]),
    });

    if (this.question.type === 'multiple-choice') {
      this.setupMultipleChoiceForm();
    }
  }

  ngOnInit(): void {
    this.sharedService.setModelOpen(true);
  }

  ngOnDestroy(): void {
    this.sharedService.setModelOpen(false);
  }

  setupMultipleChoiceForm(): void {
    const multipleChoicesArray = this.form.get('multipleChoices') as FormArray;

    // Clear existing controls
    while (multipleChoicesArray.length) {
      multipleChoicesArray.removeAt(0);
    }

    // Add a FormControl for each option
    this.question.options.forEach(() => {
      multipleChoicesArray.push(new FormControl(false));
    });
  }

  get multipleChoicesFormArray(): FormArray {
    return this.form.get('multipleChoices') as FormArray;
  }

  onContinue(): void {
    if (this.question.type === 'information') {
      this.result.correct = true;
      this.result.nextQuestionId = this.question.nextQuestionId;
      this.dialogRef.close(this.result);
      return;
    }

    this.validateAnswer();
  }

  validateAnswer(): void {
    if (this.question.type === 'single-choice') {
      const selectedOptionIndex = parseInt(
        this.form.get('singleChoice')?.value,
        10
      );
      const selectedOption = this.question.options[selectedOptionIndex];

      this.result.correct = selectedOption.isCorrect ?? false;
      this.result.message = selectedOption.message;
      this.result.nextTimeCode = selectedOption.nextTimeCode;
      this.result.nextQuestionId = this.question.nextQuestionId;

      this.showRationale = true;
    } else if (this.question.type === 'multiple-choice') {
      const selectedValues = this.multipleChoicesFormArray.controls
        .map((control, index) => {
          return { selected: control.value, index };
        })
        .filter((item) => item.selected)
        .map((item) => item.index);

      // Check if the user selected all correct options and only correct options
      const correctOptions = this.question.options
        .map((option, index) => ({ isCorrect: option.isCorrect, index }))
        .filter((item) => item.isCorrect)
        .map((item) => item.index);

      const allCorrectSelected = correctOptions.every((index) =>
        selectedValues.includes(index)
      );
      const onlyCorrectSelected = selectedValues.every((index) =>
        correctOptions.includes(index)
      );

      this.result.correct = allCorrectSelected && onlyCorrectSelected;
      this.result.message = this.question.rationale;
      this.result.nextQuestionId = this.question.nextQuestionId;

      if (selectedValues.length > 0) {
        // Get the nextTimeCode from the first selected option
        this.result.nextTimeCode =
          this.question.options[selectedValues[0]].nextTimeCode;
      }

      this.showRationale = true;
    }
  }

  closeDialog(): void {
    this.dialogRef.close(this.result);
  }

  isSubmitDisabled(): boolean {
    if (this.question.type === 'single-choice') {
      return this.form.get('singleChoice')?.value === '';
    } else if (this.question.type === 'multiple-choice') {
      return !this.multipleChoicesFormArray.controls.some(
        (control) => control.value
      );
    }
    return false;
  }
}
